import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import { useSettings } from "@/contexts/SettingsContext";
import { resolveImage } from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import Seo from "@/components/Seo";

const DEFAULT_DETAILS = {
  "Brand Activation": {
    id: "brand-activation",
    tagline: "Make strangers into fans.",
    points: [
      "Photo & video experiences — from the iconic Vespa booth to 120° bullet-time rigs",
      "AI face swap & generative content moments",
      "Live personalisation (t-shirts, tote bags, merch) using transfer presses",
      "AR / VR branded experiences — shareable, measurable, unforgettable",
      "Interactive games and mosaic reveals",
    ],
    cta: "View activation products",
    ctaLink: "/products?category=Brand+Activation",
    landingLinks: [
      { to: "/brand-activation-belgium", label: "Brand activation in Belgium" },
      { to: "/photobooth-rental-belgium", label: "Photobooth rental in Belgium" },
    ],
  },
  "Event Technology": {
    id: "event-technology",
    tagline: "The invisible backbone of flawless events.",
    points: [
      "Branded online registration flows with payments and automated communications",
      "On-site check-in, QR scanning, RFID, and award-winning badge printing",
      "Access control with multi-zone logic and live attendance dashboards",
      "Mobile voting, polls and Q&A that drive stage interaction",
      "Lead-tracking tools for exhibitors that turn conversations into pipeline",
    ],
    cta: "View event tech products",
    ctaLink: "/products?category=Event+Technology",
    landingLinks: [
      { to: "/event-registration-belgium", label: "Event registration in Belgium" },
      { to: "/onsite-badging-belgium", label: "Onsite badging in Belgium" },
      { to: "/event-accreditation-systems", label: "Event accreditation systems" },
    ],
  },
  "IT & AV Rental": {
    id: "it-av-rental",
    tagline: "Everything that plugs in, delivered.",
    points: [
      "Pre-configured laptops, tablets and printers for workshops, training rooms, pop-ups",
      "4K screens, touch displays, and LED walls — with install and breakdown",
      "Full AV: sound systems, projectors, microphones, stage kits",
      "Pan-European delivery with on-site engineers",
      "Insured inventory and 24/7 hotline during events",
    ],
    cta: "View rental products",
    ctaLink: "/products?category=IT+%26+AV+Rental",
    landingLinks: [],
  },
};

const ACCENT_MAP = { peach: "bg-ot-peach", mint: "bg-ot-mint", coral: "bg-ot-coral" };

export default function Services() {
  const { settings } = useSettings();
  usePageTitle("services");
  if (!settings) return null;

  return (
    <div data-testid="services-page">
      <Seo
        title="Event Technology, Brand Activation & IT/AV Rental Services in Belgium"
        path="/services"
        description="Discover OneTec's three service pillars in Belgium: brand activation (photobooths, VR, AI face swap), event technology (online registration, onsite badging, event accreditation systems, access control), and IT & AV rental (laptops, LED walls, screens). Pan-European delivery from Zaventem."
      />
      <section className="border-b-2 border-ot-fg">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <p className="text-xs uppercase tracking-widest font-bold text-ot-indigo">Services</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[8vw] uppercase tracking-tighter leading-[0.88] mt-4">
            Three pillars.<br />
            <span className="bg-ot-peach border-2 border-ot-fg px-3">End-to-end.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg md:text-xl text-ot-fg/80">
            From the first registration email to the last badge collected — and everything loud, shiny and shareable in between.
          </p>
        </div>
      </section>

      {(settings.services || []).map((s, i) => {
        const d = DEFAULT_DETAILS[s.title] || {};
        return (
          <section
            key={s.title}
            id={d.id || s.title.toLowerCase().replace(/\s+/g, "-")}
            className="border-b-2 border-ot-fg"
            data-testid={`service-${d.id || "service"}`}
          >
            <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-10 items-center">
              <motion.div
                className={`md:col-span-5 ${i % 2 === 1 ? "md:order-2" : ""}`}
                initial={{ opacity: 0, x: i % 2 === 1 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className={`${ACCENT_MAP[s.accent] || "bg-ot-peach"} border-2 border-ot-fg aspect-[4/5] relative overflow-hidden shadow-[10px_10px_0px_#111111]`}>
                  {s.image_url && (
                    <img
                      src={resolveImage(s.image_url)}
                      alt={s.title}
                      className="absolute inset-0 w-full h-full object-cover mix-blend-multiply"
                    />
                  )}
                </div>
              </motion.div>

              <div className="md:col-span-7">
                <p className="font-display text-sm uppercase tracking-widest text-ot-fg/50">0{i + 1} / 0{(settings.services || []).length}</p>
                <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-none mt-2">
                  {s.title}
                </h2>
                <p className="font-display text-2xl md:text-3xl mt-4 text-ot-fg/80">{d.tagline || s.description}</p>
                <p className="mt-4 text-lg text-ot-fg/80">{s.description}</p>
                {d.points && (
                  <ul className="mt-8 space-y-3">
                    {d.points.map((p) => (
                      <li key={p} className="flex gap-3 items-start">
                        <CheckCircle size={22} weight="fill" className="text-ot-fg mt-1 shrink-0" />
                        <span className="text-lg">{p}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to={d.ctaLink || "/products"} className="nb-btn" data-testid={`service-cta-${d.id || i}`}>
                    {d.cta || "See products"} <ArrowUpRight weight="bold" />
                  </Link>
                  <Link to="/contact" className="nb-btn nb-btn-ghost">
                    Talk to us <ArrowUpRight weight="bold" />
                  </Link>
                </div>
                {d.landingLinks && d.landingLinks.length > 0 && (
                  <div className="mt-6 pt-5 border-t-2 border-ot-fg/15">
                    <p className="text-xs uppercase tracking-widest font-bold text-ot-indigo mb-3">
                      Learn more
                    </p>
                    <ul className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
                      {d.landingLinks.map((ll) => (
                        <li key={ll.to}>
                          <Link
                            to={ll.to}
                            className="underline hover:text-ot-indigo decoration-ot-fg/40 hover:decoration-ot-indigo"
                          >
                            {ll.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </section>
        );
      })}

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
        <div className="bg-ot-indigo text-white border-2 border-ot-fg p-10 md:p-20 text-center shadow-[12px_12px_0px_#f0cfb0]">
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
            One team. <span className="text-ot-peach">Every discipline.</span>
          </h2>
          <Link to="/contact" className="nb-btn bg-ot-peach text-ot-fg border-ot-peach mt-8">
            Brief us <ArrowUpRight weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
